import { ApolloError, useMutation } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'ramda';
import React, { FC, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';

import SsnInput from '@ui/forms/SsnInput';

import BackAndNextNav from '@ui/molecules/BackAndNextNav';
import SpacedGridItem from '@ui/molecules/SpacedGridItem';

import spaced from '@ui/styled/Spaced';

import useMemberInfo from '@hooks/useMemberInfo';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';
import { IAccountInfoMutationData, IAccountInfoMutationResponse } from '@folx-types/member';

import ACCOUNT_INFO_MUTATION from '@graphql/mutations/AccountInfo.mutation.gql';

import { dataConstants, schema } from './SsnInputStep.data';

export interface ISsnInputStepPageContent {
	ssnInputLabel: string;
	ssnInputHelper: string;
	securityText: string;
}

interface ISsnInputStepProps extends IFlowStepComponentProps {
	pageContent: ISsnInputStepPageContent;
}

const SpacedFineprint = spaced(BasicText);

const SsnInputStep: FC<ISsnInputStepProps> = ({ pageContent, stepKey }) => {
	const { ssnInputLabel, ssnInputHelper, securityText } = pageContent;

	const { existingFormData, handleFormSubmission, handleNavBack } = useContext(FlowContext);
	const { memberInfo } = useMemberInfo();
	const [updateAccountInfo] = useMutation<IAccountInfoMutationResponse>(ACCOUNT_INFO_MUTATION);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			[dataConstants.SSN]: existingFormData?.ssn ?? '',
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		formState: { errors },
	} = form;

	const onSubmit = async (data: Record<string, unknown>) => {
		const ssn = data?.[dataConstants.SSN] as string | undefined;
		const updateMemberAccountInfo = async (input: IAccountInfoMutationData) => {
			await updateAccountInfo({
				variables: input,
			}).catch((error: ApolloError) => {
				datadogRum.addError(
					`We've run into a problem updating account information for member ${memberInfo?.id}. ${error}`,
					error
				);
			});
		};

		if (!isNil(memberInfo?.id)) {
			await updateMemberAccountInfo({
				clientMutationId: memberInfo.id,
				memberId: memberInfo.id,
				patch: {
					ssn: ssn,
				},
			});
		}

		handleFormSubmission(data, stepKey);
	};

	return (
		<SpacedGridItem marginBottom="lgxl">
			<form onSubmit={handleSubmit(onSubmit)}>
				<Controller
					name={dataConstants.SSN}
					control={control}
					render={({ field: { value, onChange, ...field } }) => (
						<SsnInput
							label={ssnInputLabel}
							subLabel={ssnInputHelper}
							onChange={onChange}
							value={value}
							error={!!errors[dataConstants.SSN]?.message}
							errorLabel={errors[dataConstants.SSN]?.message ?? ''}
							{...field}
						/>
					)}
				/>
				<SpacedFineprint variant="fineprint" marginTop="sm">
					{securityText}
				</SpacedFineprint>
				<BackAndNextNav
					marginTop="xxl"
					submitLabel="Next"
					backOnClick={() => handleNavBack(stepKey)}
				/>
			</form>
		</SpacedGridItem>
	);
};

export default SsnInputStep;
